import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  ButtonColor,
  Loader,
  NotificationType,
  PortfolioGrid,
} from '../../../../components'
import {
  GET_PORTFOLIO_BY_CREATOR,
  GetPortfolioByCreatorResponse,
  REAPPLY_CREATOR,
} from '../../../../gql'
import {
  Creator,
  GetPortfolioSort,
  MutationReapplyCreatorArgs,
  QueryGetPortfolioByCreatorArgs,
} from '../../../../gql/types'
import { useNotification } from '../../../../utils/hooks'

interface Props {
  creator: Creator
  onBack: () => void
}

const ReapplyReview = ({ creator, onBack }: Props) => {
  const { addNotification } = useNotification()

  const { loading: getPortfolioLoading, data: getPortfolioData } = useQuery<
    GetPortfolioByCreatorResponse,
    QueryGetPortfolioByCreatorArgs
  >(GET_PORTFOLIO_BY_CREATOR, {
    variables: {
      creatorId: creator.id,
      options: {
        skip: 0,
        limit: 12,
        sort: GetPortfolioSort.newest,
      },
    },
  })

  const [reapplyCreator, { loading: reapplyLoading }] = useMutation<
    { reapplyCreator: Creator },
    MutationReapplyCreatorArgs
  >(REAPPLY_CREATOR)

  const portfolioItemsCount =
    getPortfolioData?.getPortfolioByCreator?.items?.length ?? 0

  const onReapply = async () => {
    try {
      await reapplyCreator({
        variables: {
          id: creator.id,
        },
      })
      addNotification(
        'Reapplication submitted successfully. Your application is now under review.',
        NotificationType.success,
      )
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  return (
    <div className='md:shadow rounded-t-md sm:overflow-hidden mb-8'>
      <div className='py-5 md:bg-white space-y-6 sm:p-6'>
        <div className='sm:mx-auto'>
          <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
            Review & Reapply
          </h2>
          <p className='mt-2 md:text-center text-sm text-gray-600'>
            Please review your updated application before submitting.
          </p>
        </div>

        <div className='space-y-6'>
          <div>
            <h3 className='text-lg font-medium text-gray-900'>
              Introduction Video
            </h3>
            {creator?.introVideo?.url ? (
              <div className='mt-2 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6'>
                <video
                  className='max-h-[350px]'
                  src={creator.introVideo.url}
                  controls
                />
              </div>
            ) : (
              <p className='mt-2 text-sm text-red-500'>
                No introduction video uploaded.
              </p>
            )}
          </div>

          <div>
            <h3 className='text-lg font-medium text-gray-900'>
              Portfolio ({portfolioItemsCount} videos)
            </h3>
            {getPortfolioLoading && <Loader />}
            {!getPortfolioLoading && getPortfolioData && (
              <div className='-mx-4 mt-2'>
                <PortfolioGrid
                  items={getPortfolioData.getPortfolioByCreator.items}
                  isFullScreen={true}
                  isDeleteAvailable={false}
                />
              </div>
            )}
          </div>
        </div>

        {(creator.resubmissionCount ?? 0) > 0 && (
          <p className='text-xs text-gray-400 text-center'>
            {creator.resubmissionCount} of {creator.maxResubmissions}{' '}
            resubmissions used
          </p>
        )}
      </div>

      <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 text-right sm:px-6'>
        <Button
          title='Go back'
          type='button'
          colour={ButtonColor.white}
          disabled={reapplyLoading}
          onClick={onBack}
        />
        <Button
          type='button'
          title='Reapply for Review'
          onClick={onReapply}
          loading={reapplyLoading}
          disabled={reapplyLoading}
          width='200px'
        />
      </div>
    </div>
  )
}

export { ReapplyReview }
